import { CollaboratingGroupSingleResponse, ICollaboratingGroup, ICollaboratingGroupContext, isFunction, isPromiseLike } from '@ngt/opms';
import { useMemo } from 'react';
import { KeyedMutator, MutatorCallback } from 'swr';
import { GetConsentEventDataResponse, CollaboratingGroup } from '../../api/dtos';
import useConsentEventData from '../data/useConsentEventData';



const useGenerateEConsentCollaboratingGroupContext = (consentEventContext: ReturnType<typeof useConsentEventData>) => {
    
    const collaboratingGroupContext: ICollaboratingGroupContext = useMemo(() => {
        const collaboratingGroup = consentEventContext.data?.collaboratingGroup as ICollaboratingGroup;
        const error = consentEventContext.error;

        const collaboratingGroupMutate: KeyedMutator<CollaboratingGroupSingleResponse> = async (data, opts) => {
            let newData: GetConsentEventDataResponse = { ...(consentEventContext.data as any), responseStatus: error as any };
            let newSingleData: CollaboratingGroupSingleResponse | undefined = undefined;
            
            if (!data) {
                newSingleData = undefined;
            }
            else if (isFunction(data)) {
                const existingSingleData = { collaboratingGroup, responseStatus: error } as CollaboratingGroupSingleResponse;

                const callback = (data as MutatorCallback<CollaboratingGroupSingleResponse>)(existingSingleData);
                
                if (isPromiseLike(callback)) {
                    newSingleData = await (callback as Promise<CollaboratingGroupSingleResponse>);
                }
                else {
                    newSingleData = callback;
                }
            }
            else if (isPromiseLike(data)) {
                newSingleData = await (data as Promise<CollaboratingGroupSingleResponse>);
            }

            if (!newSingleData) {
                newData.collaboratingGroup = undefined as any;
            }
            else {
                if (newSingleData.responseStatus) {
                    newData.responseStatus = newSingleData.responseStatus;
                }

                newData.collaboratingGroup = newSingleData.collaboratingGroup as CollaboratingGroup;
            }

            const updatedData = await consentEventContext.mutate(newData, opts as any);

            if (updatedData) {
                return {
                    collaboratingGroup: updatedData.collaboratingGroup,
                    responseStatus: updatedData.responseStatus
                } as CollaboratingGroupSingleResponse
            }

            return undefined;
        }

        const collaboratingGroupRefresh = async () => {
            const newData = await consentEventContext.refresh();

            return newData?.collaboratingGroup;
        }

        return {
            data: collaboratingGroup,
            error: consentEventContext.error,
            loading: consentEventContext.loading,
            validating: consentEventContext.validating,
            mutate: collaboratingGroupMutate,
            refresh: collaboratingGroupRefresh
        }
    }, [consentEventContext])

    return collaboratingGroupContext
}

export default useGenerateEConsentCollaboratingGroupContext;